import { get } from "lodash";

import { FilterFieldOptionsHookResult } from "~/features/filter";
import { useGetLocationData } from "~/hooks/locationData";
import { onScrollBottomDealers } from "~/hooks/selectDealerValue";
import { onScrollBottomViolationTypes } from "~/hooks/selectViolationTypeValue";
import { language } from "~/libs/getData";
import {
  useGetDealerPanelQuery,
  useGetDealersFederalAreaQuery,
  useGetDealersRegionsQuery,
} from "~/schemas/generated/graphql";
import { usePermissions } from "~/shared/context/PermissionContext";

export const useDealerOptions = (): FilterFieldOptionsHookResult => {
  const isEn = language === "en";
  const { dealerIdFilter } = useGetLocationData() as Record<string, unknown>;
  const selectedDealerId = dealerIdFilter ? String(dealerIdFilter) : null;

  const {
    dealersForSelect,
    introspectionHandler,
    searchDealer,
    GetViolationTypesLoading,
  } = onScrollBottomDealers();

  const options = dealersForSelect.map(({ value, label }) => ({
    value: String(value),
    label,
  }));

  const needSelectedDealer =
    !!selectedDealerId &&
    !options.some((option) => option.value === selectedDealerId);
  const { data: selectedDealerData } = useGetDealerPanelQuery({
    variables: { DealerId: selectedDealerId },
    skip: !needSelectedDealer,
  });
  if (needSelectedDealer) {
    const dealer = get(selectedDealerData, "DealerGetter.getDealer") as
      | { DealerName?: string; DealerNameEn?: string }
      | undefined;
    const dealerName = isEn ? dealer?.DealerNameEn : dealer?.DealerName;
    if (dealerName) {
      options.unshift({ value: selectedDealerId, label: dealerName });
    }
  }

  return {
    options,
    loading: GetViolationTypesLoading,
    onScrollBottom: introspectionHandler,
    searchOptions: searchDealer,
  };
};

export const useViolationTypeOptions = (): FilterFieldOptionsHookResult => {
  const {
    violationTypesForSelect,
    introspectionHandler,
    searchType,
    GetViolationTypesLoading,
  } = onScrollBottomViolationTypes();

  // Первый элемент — служебный пункт-заглушка из хука, в фильтре он не нужен
  return {
    options: violationTypesForSelect.slice(1).map(({ value, label }) => ({
      value: String(value),
      label,
    })),
    loading: GetViolationTypesLoading,
    onScrollBottom: introspectionHandler,
    searchOptions: searchType,
  };
};

export const useFederalAreaOptions = (): FilterFieldOptionsHookResult => {
  const { hasPermission, loading: loadingPermissions } = usePermissions();
  const isEn = language === "en";

  const { data, loading } = useGetDealersFederalAreaQuery({
    skip: loadingPermissions || !hasPermission("dealergraph.federalArea.get"),
  });

  const items: string[] = get(
    data,
    `DealerGetter.getDealersFederalArea.${isEn ? "itemsEn" : "items"}`,
    [],
  );

  return {
    options: items.map((item) => ({ value: item, label: item })),
    loading,
  };
};

export const useRegionOptions = (): FilterFieldOptionsHookResult => {
  const { hasPermission, loading: loadingPermissions } = usePermissions();
  const isEn = language === "en";

  const { data, loading } = useGetDealersRegionsQuery({
    skip: loadingPermissions || !hasPermission("dealergraph.regions.get"),
  });

  const items: string[] = get(
    data,
    `DealerGetter.getDealersRegions.${isEn ? "itemsEn" : "items"}`,
    [],
  );

  return {
    options: items.map((item) => ({ value: item, label: item })),
    loading,
  };
};
